import "../features/eex/operations-pages.css";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { Td, Th } from "../components/eex/ui";
import { useEexRole } from "../components/eex/use-eex-role";
import { fmtPx as px } from "../lib/eex-format";
import { useMobileUiV1State } from "../hooks/use-media-query";
import {
  type DeliveryContract,
  type DeliveryResult,
} from "../features/eex/delivery/contracts";

export const Route = createFileRoute("/eex/delivery")({
  component: EexDeliveryPage,
});

const percent = (value: number | null): string =>
  value == null ? "—" : `${(value * 100).toFixed(1)}%`;

function EexDeliveryPage() {
  return (
    <div className="operations-workspace ops-page ops-delivery min-w-0 space-y-4 px-3 py-4 sm:space-y-6 sm:p-4">
      <header className="ops-heading min-w-0">
        <h1 className="font-semibold text-lg">EEX Delivery</h1>
        <p className="text-muted text-xs max-w-3xl">
          Month positions in or just out of delivery — realized day-ahead average, fixed share, the
          remainder price implied by the official settlement, and the final-settlement benchmark check.
        </p>
      </header>
      <DeliveryPanel />
    </div>
  );
}

// In-delivery months (realized DA average, fixed share, the remainder price
// implied by the official settlement) and recently completed months (our
// quarter-hour benchmark mean vs the final settlement the clearing house used).
function DeliveryPanel() {
  const { isAdmin } = useEexRole();
  const { isMobileViewport } = useMobileUiV1State();
  const deliveryQ = useQuery({
    queryKey: ["eex-delivery"],
    queryFn: () => api<DeliveryResult>("/eex/delivery"),
  });
  const data = deliveryQ.data;
  if (deliveryQ.isLoading) return <div className="text-muted text-sm">Loading delivery view…</div>;
  if (deliveryQ.isError) return <div className="text-negRed text-sm">{(deliveryQ.error as Error).message}</div>;
  if (!data) return null;

  const inDelivery = data.contracts.filter((contract) => contract.status === "in_delivery");
  const completed = data.contracts.filter((contract) => contract.status === "completed");
  const contracts = [...inDelivery, ...completed];
  return (
    <section className="ops-delivery-ledger min-w-0 space-y-2">
      <h2 className="ops-section-title text-xs font-semibold text-muted uppercase tracking-wide">Delivery</h2>
      <div className="min-w-0 break-words text-xs text-muted">
        <span className="sm:hidden">ENTSO-E day-ahead data: </span>
        <span className="hidden sm:inline">Day-ahead data: </span>
        {data.zones.length === 0
          ? "none yet — the cron fills a 35-day window once an ENTSO-E token is set"
          : data.zones.map((z) => `${z.zone} ${z.days}d (to ${z.latest ?? "—"})`).join(" · ")}
      </div>
      <div className={cn("min-w-0 break-words text-xs sm:hidden", settlementFeedToneClass(data.settlementFeed))}>
        ECC SPAN settlement fallback: {data.settlementFeed.status.replace("_", " ")}
        {` · imported ${data.settlementFeed.latestImportedBusinessDate ?? "—"}`}
        {` · discovered ${data.settlementFeed.latestDiscoveredBusinessDate ?? "—"}`}
        {` · checked ${data.settlementFeed.lastCheckedAt ?? "—"}`}
        {` · ${data.settlementFeed.reason}`}
        {data.settlementFeed.eccMarksSuppressed ? " · ECC marks suppressed" : ""}
      </div>

      {contracts.length === 0 ? (
        <div className="text-muted text-sm">No month positions in or just out of delivery.</div>
      ) : (
        <>
          <div className="ops-table-frame hidden max-w-full overflow-x-auto rounded-md border border-border/60 sm:block">
          <table className="w-full text-xs">
            <thead className="bg-border/20 text-left">
              <tr>
                <Th>Contract</Th>
                <Th className="text-right">Pos MW</Th>
                <Th className="text-right">MWh</Th>
                <Th className="text-right">Fixed</Th>
                <Th className="text-right">Realized avg</Th>
                <Th className="text-right">Implied rest</Th>
                <Th className="text-right">Last settle</Th>
                <Th>FSP check</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr
                  key={`${c.code}-${c.expYear}-${c.expMonth}`}
                  className={cn("border-t border-border/40", c.status === "completed" && "opacity-75")}
                >
                  <Td>
                    {c.label}{" "}
                    <span className="font-mono text-muted">
                      {c.status === "in_delivery" ? "in delivery" : "completed"}
                    </span>
                  </Td>
                  <Td className="text-right tabular-nums font-semibold">{c.netPos}</Td>
                  <Td className="text-right tabular-nums">{c.mwh.toLocaleString("en-US")}</Td>
                  <Td className="text-right tabular-nums">{percent(c.progress?.fixedShare ?? (c.status === "completed" ? 1 : null))}</Td>
                  <Td className="text-right tabular-nums">{px(c.progress?.realizedAvg ?? c.fsp?.benchmarkMean ?? null, 2)}</Td>
                  <Td className="text-right tabular-nums">{px(c.progress?.impliedRemainder ?? null, 2)}</Td>
                  <Td className="text-right tabular-nums">
                    {px(c.lastDsp, 2)}
                    {c.lastDspDate && <span className="text-muted"> ({c.lastDspDate.slice(5)})</span>}
                  </Td>
                  <Td>
                    {c.fsp == null ? (
                      <span className="text-muted">—</span>
                    ) : c.fsp.status === "match" ? (
                      <span className="text-posGreen">✓ {px(c.fsp.benchmarkMean, 2)} = FSP</span>
                    ) : c.fsp.status === "mismatch" ? (
                      <span className="text-negRed font-semibold">
                        ✗ {px(c.fsp.benchmarkMean, 2)} vs {px(c.fsp.finalSettlement, 2)}
                      </span>
                    ) : (
                      <span className="text-muted">
                        incomplete ({c.fsp.quartersCovered}/{c.fsp.quartersExpected} qh)
                      </span>
                    )}
                  </Td>
                  <Td className="ops-delivery-notes text-muted">
                    {c.zone == null && <span className="block sm:inline">no DA benchmark (PUN/gas)</span>}
                    {c.awaitingFsp && <span className="block sm:inline">awaiting final settlement</span>}
                    {c.progress != null && c.progress.missingDays > 0 && (
                      <span className="text-negRed">{c.progress.missingDays} day(s) missing DA data</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="space-y-2 sm:hidden" aria-label="Delivery obligations">
            {contracts.map((contract) => (
              <DeliveryContractCard
                key={`${contract.code}-${contract.expYear}-${contract.expMonth}`}
                contract={contract}
                zoneCoverage={data.zones.find((item) => item.zone === contract.zone) ?? null}
              />
            ))}
          </div>
        </>
      )}

      {isAdmin && !isMobileViewport && <DamBackfillBar />}
    </section>
  );
}

function settlementFeedToneClass(feed: DeliveryResult["settlementFeed"]): string {
  if (feed.status === "unavailable" || feed.eccMarksSuppressed) return "text-negRed";
  if (feed.status === "catching_up") return "text-warnOrange";
  return "text-posGreen";
}

function DeliveryContractCard({
  contract,
  zoneCoverage,
}: {
  contract: DeliveryContract;
  zoneCoverage: DeliveryResult["zones"][number] | null;
}) {
  const progress = contract.progress;
  const fsp = contract.fsp;
  const hasBenchmark = contract.zone !== null;
  const missingDays = progress?.missingDays ?? 0;

  return (
    <details className="ops-panel ops-delivery-contract group min-w-0 rounded-md border border-border/60">
      <summary className="flex min-h-11 cursor-pointer touch-manipulation items-center gap-3 px-3 py-2">
        <span aria-hidden="true" className="shrink-0 text-muted transition-transform group-open:rotate-90">▸</span>
        <span className="min-w-0 flex-1">
          <span className="block break-words font-semibold text-fg">{contract.label}</span>
          <span className="block break-all font-mono text-xs text-muted">
            {contract.code} · {contract.status.replace("_", " ")}
          </span>
          <span className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
            {!hasBenchmark && <span className="text-muted">no DA benchmark</span>}
            {fsp && (
              <span className={fsp.status === "match" ? "text-posGreen" : fsp.status === "mismatch" ? "text-negRed" : "text-muted"}>
                FSP {fsp.status}
              </span>
            )}
            {missingDays > 0 && <span className="text-negRed">{missingDays} day(s) missing</span>}
            {contract.awaitingFsp && <span className="text-muted">awaiting FSP</span>}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className={cn("block tabular-nums", contract.netPos < 0 ? "text-negRed" : "text-fg")}>
            {contract.netPos > 0 ? "+" : ""}{contract.netPos} MW
          </span>
        </span>
      </summary>
      <div className="min-w-0 space-y-4 border-t border-border/40 p-3">
        {(!hasBenchmark || fsp?.status === "mismatch" || fsp?.status === "incomplete" || missingDays > 0 || contract.awaitingFsp) && (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {!hasBenchmark && <li className="text-muted">No day-ahead benchmark (PUN/gas)</li>}
            {fsp?.status === "mismatch" && <li className="text-negRed">FSP mismatch</li>}
            {fsp?.status === "incomplete" && <li className="text-muted">FSP incomplete</li>}
            {missingDays > 0 && <li className="text-negRed">{missingDays} day(s) missing day-ahead data</li>}
            {contract.awaitingFsp && <li className="text-muted">Awaiting final settlement</li>}
          </ul>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
          <DeliveryFact label="Delivery" value={`${contract.start} → ${contract.end}`} wide />
          <DeliveryFact label="Position" value={`${contract.netPos} MW`} />
          <DeliveryFact label="Quantity" value={`${contract.mwh.toLocaleString("en-US")} MWh`} />
          <DeliveryFact label="Canonical hours" value={contract.hours.toLocaleString("en-US")} />
          <DeliveryFact label="Benchmark zone" value={contract.zone ?? "—"} />
          <DeliveryFact label="Fixed" value={percent(progress?.fixedShare ?? (contract.status === "completed" ? 1 : null))} />
          <DeliveryFact label="Realized average" value={px(progress?.realizedAvg ?? fsp?.benchmarkMean ?? null, 2)} />
          <DeliveryFact label="Implied remainder" value={px(progress?.impliedRemainder ?? null, 2)} />
          <DeliveryFact label="Last settlement" value={px(contract.lastDsp, 2)} />
          <DeliveryFact label="Settlement date" value={contract.lastDspDate ?? "—"} />
        </dl>

        {progress && (
          <section className="ops-delivery-ledger min-w-0 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Delivery progress</h3>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 rounded border border-border/40 p-3">
              <DeliveryFact label="Days realized" value={`${progress.realizedDays} / ${progress.totalDays}`} />
              <DeliveryFact
                label="Missing days"
                value={`${progress.missingDays}`}
                className={progress.missingDays > 0 ? "text-negRed" : undefined}
              />
              <DeliveryFact label="Quarters realized" value={`${progress.realizedQuarters} / ${progress.totalQuarters}`} />
              <DeliveryFact label="Projected FSP" value={px(progress.projectedFsp, 2)} />
            </dl>
          </section>
        )}

        <section className="ops-delivery-ledger min-w-0 space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Final settlement check</h3>
          {fsp ? (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 rounded border border-border/40 p-3">
              <DeliveryFact label="Status" value={fsp.status} className={fsp.status === "match" ? "text-posGreen" : "text-negRed"} />
              <DeliveryFact label="Coverage" value={`${fsp.quartersCovered} / ${fsp.quartersExpected} qh`} />
              <DeliveryFact label="Benchmark mean" value={px(fsp.benchmarkMean, 2)} />
              <DeliveryFact label="Final settlement" value={px(fsp.finalSettlement, 2)} />
              <DeliveryFact label="Difference" value={px(fsp.diff, 2)} className={fsp.status === "mismatch" ? "text-negRed" : undefined} />
            </dl>
          ) : (
            <div className="rounded border border-border/40 p-3 text-sm text-muted">
              {contract.awaitingFsp ? "Awaiting final settlement." : "No final-settlement check yet."}
            </div>
          )}
        </section>

        <section className="ops-delivery-ledger min-w-0 space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Source data</h3>
          <div className="min-w-0 space-y-3 rounded border border-border/40 p-3">
            {contract.zone ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <DeliveryFact label="Day-ahead source" value="ENTSO-E" wide />
                <DeliveryFact label="Zone" value={contract.zone} />
                <DeliveryFact label="Available days" value={zoneCoverage ? `${zoneCoverage.days}` : "—"} />
                <DeliveryFact label="Available through" value={zoneCoverage?.latest ?? "—"} wide />
              </dl>
            ) : (
              <div className="text-sm text-muted">No day-ahead benchmark for this contract.</div>
            )}
          </div>
        </section>
      </div>
    </details>
  );
}

function DeliveryFact({
  label,
  value,
  wide,
  className,
}: {
  label: string;
  value: string;
  wide?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", wide && "col-span-2")}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt>
      <dd className={cn("break-words text-sm font-medium tabular-nums text-fg", className)}>{value}</dd>
    </div>
  );
}

function DamBackfillBar() {
  const qc = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const backfill = useMutation({
    mutationFn: () =>
      api<{ zones: Record<string, { filled: number; alreadyComplete: number; empty: number; errors: number }> }>(
        "/eex/dam/backfill",
        { method: "POST", body: JSON.stringify({ from, to }) }
      ),
    onSuccess: (r) => {
      setMsg(
        Object.entries(r.zones)
          .map(([z, s]) => `${z}: +${s.filled} filled, ${s.alreadyComplete} ok, ${s.empty} empty, ${s.errors} err`)
          .join(" · ")
      );
      void qc.invalidateQueries({ queryKey: ["eex-delivery"] });
    },
    onError: (e) => setMsg((e as Error).message),
  });
  return (
    <div className="ops-toolbar flex items-center gap-2 flex-wrap text-xs">
      <span className="text-muted">Backfill day-ahead prices (≤31 days per run):</span>
      <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-transparent border border-border/60 rounded px-2 py-1" />
      <span className="text-muted">→</span>
      <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-transparent border border-border/60 rounded px-2 py-1" />
      <button
        type="button"
        disabled={!from || !to || backfill.isPending}
        onClick={() => backfill.mutate()}
        className="ops-primary rounded px-3 py-1 bg-accent text-white disabled:opacity-40"
      >
        {backfill.isPending ? "Fetching…" : "Backfill"}
      </button>
      {msg && <span className="font-mono text-muted">{msg}</span>}
    </div>
  );
}
